import {
  FieldValues,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { IUser, IUserProfile } from "@sso/users/contracts";
import { Grid } from "@material-ui/core";
import {
  ApiLoadingSpinner,
  Button,
  GraphQLValidationErrors,
  HelperText,
  TextField,
  Title,
} from "@funcional/react-ui";
import { IGraphQLError } from "@common/contracts";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { v4 as uuid } from "uuid";
import { UserProfilesForm } from "./UserProfilesForm";
import { useUserProfiles } from "../hooks/useUserProfiles";

const containerSpacing = 3;

const schema = yup.object().shape({
  name: yup
    .string()
    .required("Campo nome é obrigatorio")
    .min(3, "O nome deve ter pelo menos 3 letras")
    .transform((name) => (name === "" ? null : name)),
  newMobileNumber: yup
    .string()
    .nullable(true)
    .max(11, "O celular tem que ter 11 numeros")
    .min(11, "O celular tem que ter 11 numeros")
    .transform((newMobileNumber) =>
      newMobileNumber === "" ? null : newMobileNumber
    ),
  profiles: yup.array().of(
    yup.object().shape({
      role: yup.string().required("obrigatório"),
      identity: yup
        .string()
        .nullable()
        .when("role", {
          is: (role) => !["MASTER", "SUPPORT"].includes(role),
          then: yup.string().required("obrigatório"),
          otherwise: yup.string().nullable(),
        }),
    })
  ),
});

export default function UserForm({
  onSubmit,
  defaults,
  isLoading,
  graphQLErrors,
}: {
  onSubmit: SubmitHandler<FieldValues>;
  defaults?: IUser;
  isLoading?: boolean;
  graphQLErrors?: IGraphQLError[];
}) {
  const { data, isLoading: isLoadingProfiles } = useUserProfiles();

  const form = useForm({
    defaultValues: {
      ...defaults,
      profiles: (defaults?.profiles || []) as IUserProfile[],
    } as any,
    resolver: yupResolver(schema),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const profilesFieldArray = useFieldArray({
    control,
    name: "profiles",
  });

  const profiles = profilesFieldArray.fields;

  const addProfile = () => {
    profilesFieldArray.append([
      {
        id: uuid(),
        role: "",
        identity: "",
      } as never,
    ]);
  };
  const deleteProfile = (row: any) => {
    if (window.confirm("Tem certeza de que deseja excluir este perfil?")) {
      profilesFieldArray.remove(row.index);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl">
      <GraphQLValidationErrors graphQLErrors={graphQLErrors} />
      <Grid container spacing={containerSpacing}>
        <Grid item xs={6}>
          <TextField
            required
            name="uuid"
            label="Uuid"
            id="uuid"
            control={control}
            defaultValue={defaults?.uuid}
            errorMessage={errors?.uuid?.message}
            helperText="Identificador alfanumérico único"
            readOnly={defaults && defaults.uuid ? true : false}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            name="identity"
            label="CPF"
            control={control}
            defaultValue={defaults?.identity}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="name"
            label="Nome"
            control={control}
            defaultValue={defaults?.name}
            errorMessage={errors?.name?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="newEmail"
            label="Email"
            control={control}
            defaultValue={defaults?.email}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="newMobileNumber"
            label="Celular (somente números)"
            control={control}
            defaultValue={defaults?.mobileNumber}
            errorMessage={errors?.newMobileNumber?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="status"
            label="Status"
            control={control}
            defaultValue={defaults?.status
              .replace("ACTIVE", "ATIVO")
              .replace("UNCONFIRMED", "INATIVO")}
            readOnly={defaults && defaults.status ? true : false}
          />
        </Grid>

        <Grid item xs={12}>
          <Title level={3}>Perfis de Acesso</Title>

          {errors?.profiles?.message ? (
            <HelperText errorMessage={errors?.profiles?.message} />
          ) : null}

          {profiles && data ? (
            <UserProfilesForm
              register={register}
              data={profiles as any}
              addProfile={addProfile}
              deleteProfile={deleteProfile}
              errors={errors}
              profiles={data}
            />
          ) : null}

          {isLoadingProfiles ? (
            <ApiLoadingSpinner message="Carregando perfis do usuário..." />
          ) : null}
        </Grid>

        <Grid item xs={6}>
          <Button color="primary" type="submit" disabled={isLoading}>
            Salvar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
